import React, { useState } from "react";
import { ArrowLeft, RotateCcw, Download, Palette } from "lucide-react";

const ColorByEmotionsGame = ({ onBack }) => {
  const [selectedEmotion, setSelectedEmotion] = useState("calm");
  const [coloredSections, setColoredSections] = useState({});

  const emotions = {
    calm: {
      color: "#60A5FA",
      name: "Calm",
      description: "Peaceful, serene, relaxed",
      shades: ["#DBEAFE", "#BFDBFE", "#93C5FD", "#60A5FA", "#3B82F6"],
    },
    happy: {
      color: "#FBBF24",
      name: "Happy",
      description: "Joyful, content, optimistic",
      shades: ["#FEF3C7", "#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B"],
    },
    energetic: {
      color: "#F97316",
      name: "Energetic",
      description: "Active, vibrant, motivated",
      shades: ["#FFEDD5", "#FED7AA", "#FDBA74", "#FB923C", "#F97316"],
    },
    peaceful: {
      color: "#10B981",
      name: "Peaceful",
      description: "Harmonious, balanced, centered",
      shades: ["#D1FAE5", "#A7F3D0", "#6EE7B7", "#34D399", "#10B981"],
    },
    creative: {
      color: "#8B5CF6",
      name: "Creative",
      description: "Inspired, imaginative, artistic",
      shades: ["#EDE9FE", "#DDD6FE", "#C4B5FD", "#A78BFA", "#8B5CF6"],
    },
    loving: {
      color: "#EC4899",
      name: "Loving",
      description: "Caring, compassionate, warm",
      shades: ["#FCE7F3", "#FBCFE8", "#F9A8D4", "#F472B6", "#EC4899"],
    },
  };

  // Simple mandala pattern (you can make this more complex)
  const mandalaPattern = [
    // Center circle
    { id: "center", cx: 200, cy: 200, r: 20, type: "circle" },

    // Inner ring
    ...Array.from({ length: 6 }, (_, i) => {
      const angle = (i * Math.PI * 2) / 6;
      return {
        id: `inner-${i}`,
        cx: 200 + Math.cos(angle) * 40,
        cy: 200 + Math.sin(angle) * 40,
        r: 15,
        type: "circle",
      };
    }),

    // Middle ring petals
    ...Array.from({ length: 12 }, (_, i) => {
      const angle = (i * Math.PI * 2) / 12;
      const x = 200 + Math.cos(angle) * 80;
      const y = 200 + Math.sin(angle) * 80;
      return {
        id: `middle-${i}`,
        points: [
          [x, y],
          [x + Math.cos(angle + 0.3) * 20, y + Math.sin(angle + 0.3) * 20],
          [x + Math.cos(angle) * 30, y + Math.sin(angle) * 30],
          [x + Math.cos(angle - 0.3) * 20, y + Math.sin(angle - 0.3) * 20],
        ],
        type: "polygon",
      };
    }),

    // Outer ring
    ...Array.from({ length: 16 }, (_, i) => {
      const angle = (i * Math.PI * 2) / 16;
      return {
        id: `outer-${i}`,
        cx: 200 + Math.cos(angle) * 130,
        cy: 200 + Math.sin(angle) * 130,
        r: 12,
        type: "circle",
      };
    }),

    // Decorative elements
    ...Array.from({ length: 8 }, (_, i) => {
      const angle = (i * Math.PI * 2) / 8;
      const x = 200 + Math.cos(angle) * 160;
      const y = 200 + Math.sin(angle) * 160;
      return {
        id: `decoration-${i}`,
        points: [
          [x, y],
          [x + 10, y - 15],
          [x, y - 10],
          [x - 10, y - 15],
        ],
        type: "polygon",
      };
    }),
  ];

  const colorSection = (sectionId) => {
    const emotion = emotions[selectedEmotion];
    const shadeIndex = Math.floor(Math.random() * emotion.shades.length);

    setColoredSections((prev) => ({
      ...prev,
      [sectionId]: emotion.shades[shadeIndex],
    }));
  };

  const resetColors = () => {
    setColoredSections({});
  };

  const downloadMandala = () => {
    const svg = document.getElementById("mandala-svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = 400;
    canvas.height = 400;

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement("a");
      link.download = "my-emotion-mandala.png";
      link.href = canvas.toDataURL();
      link.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return (
    <div className="min-h-screen  p-4">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white/80 rounded-xl hover:bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <h1 className="text-lg sm:text-2xl font-bold text-gray-800 text-center flex-1 mx-2">
          <span className="hidden sm:inline">🌀 Color by Emotions</span>
          <span className="sm:hidden">🌀 Color</span>
        </h1>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={downloadMandala}
            className="bg-green-500 hover:bg-green-600 text-white px-2 sm:px-3 py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Save</span>
          </button>
          <button
            onClick={resetColors}
            className="bg-red-500 hover:bg-red-600 text-white px-2 sm:px-3 py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto">
        <div className="lg:w-80 space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Current Emotion
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {Object.entries(emotions).map(([key, emotion]) => (
                <button
                  key={key}
                  onClick={() => setSelectedEmotion(key)}
                  className={`text-left p-3 rounded-lg transition-all ${
                    selectedEmotion === key
                      ? "bg-gradient-to-r from-indigo-100 to-purple-100 border-2 border-indigo-300 transform scale-105"
                      : "bg-white/50 hover:bg-white/70 border-2 border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: emotion.color }}
                    />
                    <div>
                      <div className="font-medium">{emotion.name}</div>
                      <div className="text-sm text-gray-600">
                        {emotion.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Color Palette</h3>
            <div className="flex flex-wrap gap-2">
              {emotions[selectedEmotion].shades.map((shade, index) => (
                <div
                  key={index}
                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: shade }}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Click on the mandala sections to color them with your current
              emotion
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Progress</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Sections Colored:</span>
                <span>
                  {Object.keys(coloredSections).length} /{" "}
                  {mandalaPattern.length}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      (Object.keys(coloredSections).length /
                        mandalaPattern.length) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">How to Use:</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Choose an emotion that resonates with you</li>
              <li>• Click on mandala sections to color them</li>
              <li>• Switch emotions as your feelings change</li>
              <li>• Focus on the present moment while coloring</li>
              <li>• Save your completed mandala</li>
            </ul>
          </div>
        </div>

        <div className="flex-1 flex justify-center">
          <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg w-full max-w-lg">
            <svg
              id="mandala-svg"
              width="400"
              height="400"
              viewBox="0 0 400 400"
              className="border border-gray-200 rounded-lg w-full h-auto"
            >
              <rect width="400" height="400" fill="white" />

              {mandalaPattern.map((section) => {
                const fillColor = coloredSections[section.id] || "#F9FAFB";
                const strokeColor = "#E5E7EB";

                if (section.type === "circle") {
                  return (
                    <circle
                      key={section.id}
                      cx={section.cx}
                      cy={section.cy}
                      r={section.r}
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth="1"
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => colorSection(section.id)}
                    />
                  );
                } else if (section.type === "polygon") {
                  const points = section.points
                    .map((p) => p.join(","))
                    .join(" ");
                  return (
                    <polygon
                      key={section.id}
                      points={points}
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth="1"
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => colorSection(section.id)}
                    />
                  );
                }
                return null;
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorByEmotionsGame;
